import React, {useImperativeHandle, useMemo, useRef, useState} from 'react'
import WizModal from '../../../../components/modal/WizModal'
import Text from '../../../../components/text/Text'
import useNavigation from '../../../../hooks/useNavigation'
import WizButton from '../../../../components/button/WizButton'
import {useTime} from '../../../../utils/time'
import WizSelectButton from '../../../../components/button/WizSelectButton'
import {ILeagueList} from '../../../../apis/league/getLeagueList'
import WizImage from '../../../../components/image/WizImage'
import {createEventApi} from '../../../../apis/event/createEventApi'
import {postUploadImage} from '../../../../apis/image/postUploadImage'

interface IProps {
    className?: string
}

const AddEventModal = ({className}: IProps, ref: any) => {
    const [eventData, setEventData] = useState<ILeagueList>()
    const [nextStep, setNextStep] = useState(false)
    const [addEventLang, setAddEventLang] = useState('ko')
    const [newThumbnail, setNewThumbnail] = useState('')
    const {goEvent} = useNavigation()
    const {convertTimestampToInput, convertInputToTimezone, convertUtc0ToUtc9} = useTime()
    const modalRef = useRef<any>()

    useImperativeHandle(ref, () => ({
        show: league => {
            setEventData(league)
            modalRef.current.show()
        },
    }))

    const languageList = useMemo(() => {
        return [
            {id: 'ko', value: 'Korean'},
            {id: 'en', value: 'English'},
        ]
    }, [])

    const addEventData = useMemo(() => {
        return {
            name: addEventLang === 'ko' ? eventData?.names?.ko : eventData?.names?.en,
            language: addEventLang,
            thumbnail: addEventLang === 'ko' ? eventData?.thumbnails?.ko : eventData?.thumbnails?.en,
            new_thumbnail: newThumbnail || '',
            url: addEventLang === 'ko' ? eventData?.start_urls?.ko : eventData?.start_urls?.en,
            start_at: convertInputToTimezone(convertTimestampToInput(eventData?.start_at)) as string,
            end_at: convertInputToTimezone(convertTimestampToInput(eventData?.end_at)) as string,
        }
    }, [eventData, addEventLang, newThumbnail])

    const newThumbnailRef = useRef<any>()

    const onChangeImage = async e => {
        const imageFile = e?.target?.files[0]

        if (imageFile) {
            const result = await postUploadImage(imageFile)
            setNewThumbnail(result?.image_url)
        }
    }

    const onClickAddEvent = async () => {
        const {data: createData} = await createEventApi({
            name: addEventData?.name,
            language: addEventLang,
            thumbnail: addEventData?.thumbnail,
            new_thumbnail: addEventData?.new_thumbnail,
            url: addEventData?.url,
            start_at: addEventData?.start_at,
            end_at: addEventData?.end_at,
        })

        if (createData?.success) {
            alert('Create Event Complete')
            goEvent()
        } else {
            alert('Crete Event Error!')
        }

        modalRef.current.close()
    }

    return (
        <>
            <WizModal title={'Add Event'} className={`${className}`} ref={modalRef}>
                {!nextStep && (
                    <div className={'text-center'}>
                        <Text className={'text-body2 text-font02'}>제작하고자 하는 Event 채널을 선택해주세요.</Text>

                        <div className={'mt-[20px]'}>
                            <WizSelectButton
                                onChange={e => setAddEventLang(e.id)}
                                data={languageList}
                                activeValue={languageList?.find(list => list.id === addEventLang)}
                                className={'mx-auto w-[80%]'}
                            />
                        </div>

                        <div className={'flex space-x-[5px] mt-[30px]'}>
                            <WizButton
                                text={'Cancel'}
                                type={'cancel'}
                                className={'flex-1'}
                                onClick={() => {
                                    modalRef?.current?.close()
                                }}
                            />
                            <WizButton
                                text={'Next'}
                                type={'primary'}
                                className={'flex-1'}
                                onClick={() => setNextStep(true)}
                            />
                        </div>
                    </div>
                )}

                {nextStep && (
                    <div className={'text-center'}>
                        <Text className={'text-body2 text-font02'}>이벤트 정보가 맞는지 확인해주세요.</Text>

                        <div className={'mt-[30px] space-y-[30px]'}>
                            <div className={'space-y-[5px]'}>
                                <Text className={'text-font03 text-body3'}>Title</Text>
                                <Text>{addEventData?.name}</Text>
                            </div>
                            <div className={'space-y-[5px]'}>
                                <Text className={'text-font03 text-body3'}>Thumbnail</Text>
                                <div
                                    className={
                                        'h-[60px] overflow-hidden w-[320px] border border-line02 rounded-[7px] mx-auto'
                                    }>
                                    {addEventData?.thumbnail ? (
                                        <WizImage
                                            src={addEventData?.thumbnail}
                                            containerClassName={'relative translate-y-[-50%] top-[50%]'}
                                        />
                                    ) : (
                                        <div className={'border border-line01 h-[100%] rounded-[7px] bg-bg_gray02'}>
                                            <Text className={'text-font03 text-body2 leading-[60px]'}>No Image</Text>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className={''}>
                                <div className={'w-[320px] mx-auto'}>
                                    <Text className={'text-body3 text-font02'}>New Thumbnail</Text>
                                    <div
                                        onClick={() => newThumbnailRef?.current?.click()}
                                        className={
                                            'border border-line02 rounded-[5px] overflow-hidden w-full min-h-[192px] max-h-[192px] mt-[10px] cursor-pointer'
                                        }>
                                        {newThumbnail && (
                                            <WizImage
                                                src={`${newThumbnail}?type=webp&width=1920`}
                                                className={'w-full'}
                                            />
                                        )}
                                    </div>
                                    <input
                                        type={'file'}
                                        onChange={e => onChangeImage(e)}
                                        ref={newThumbnailRef}
                                        className={'hidden'}
                                    />
                                </div>
                            </div>

                            <div className={'space-y-[5px]'}>
                                <Text className={'text-font03 text-body3'}>Event Url</Text>
                                <Text
                                    onClick={() => window.open(addEventData?.url)}
                                    className={'truncate whitespace-nowrap text-sub_blue underline cursor-pointer'}>
                                    {addEventData?.url}
                                </Text>
                            </div>
                            <div className={'grid grid-cols-2'}>
                                <div>
                                    <Text className={'text-font03 text-body3'}>Start At</Text>
                                    <Text>{convertUtc0ToUtc9(addEventData?.start_at)}</Text>
                                </div>
                                <div>
                                    <Text className={'text-font03 text-body3'}>End At</Text>
                                    <Text>{convertUtc0ToUtc9(addEventData?.end_at)}</Text>
                                </div>
                            </div>
                        </div>
                        <div className={'flex space-x-[5px] mt-[30px]'}>
                            <WizButton
                                text={'Back'}
                                type={'cancel'}
                                className={'flex-1'}
                                onClick={() => {
                                    setNextStep(false)
                                }}
                            />
                            <WizButton
                                text={'Add Event'}
                                type={'blue'}
                                className={'flex-1'}
                                onClick={() => onClickAddEvent()}
                            />
                        </div>
                    </div>
                )}
            </WizModal>
        </>
    )
}

export default React.forwardRef(AddEventModal)
